import React, { useEffect, useRef, useState } from 'react';
import { AvailabilityOptions, FulfilmentOptionsProps, StoreAvailabilityDetails } from '../utils/types';
import Availability from '../default-availability/Availability';
import { getFulfilmentClient } from '../../../../api/fulfilment/getFulfilmentClient';
import { OptionNames } from '../../../../api/fulfilment/utils';
import PopoutDrawer from '../../popout-drawer/PopoutDrawer';
import { DeliveryLocation, StoreLocation } from '../../localisation/utils/types';
import LocationSearch from '../../location-search/LocationSearch';
import { useLocalisationContext } from '../../localisation/context/LocalisationContext';
import { generateCashOrCollectOptions, generateDeliveryOptions } from '../utils/availabilityUtils';
import StoreAvailability from '../store-availability/StoreAvailability';
import DeliveryAvailability from '../delivery-availability/DeliveryAvailability';
import { styles } from '../styles/Availability.css';
import {
  ERROR_MESSAGE,
  ERROR_MESSAGE_DELIVERY,
  ERROR_MESSAGE_PICKUP,
  ERROR_MESSAGE_UNAVAILABLE,
  NO_DELIVERY_AVAILABLE_MESSAGE,
  NO_INSTORE_AVAILABLE_MESSAGE,
  NO_INSTORE_AVAILABLE_MESSAGE_CART,
  ONLY_INSTORE_AVAILABLE_MESSAGE,
} from '../utils/constants';
import { trackingContext, useTracking } from '@jbhi-fi/components/src/Tracking';
import { getAggregatedStoreOptions, transformPickupOptions } from '../../localisation/utils/productFulfilmentUtils';
import { useQueryClient } from 'react-query';
import { getLocal, setLocal, LocalStorageKeys } from '../../../../utils/localStorage';
import { useRecaptchaContext } from '@jbhi-fi/components/src/context/RecaptchaProvider';
import { FULFILMENT_API_RECAPTCHA_ACTION } from '@jbhi-fi/apis/src/fulfilment/FulfilmentClient';
import { FulfilmentOptionsLineItem, FulfilmentOptionsResponse } from '@jbhi-fi/apis/src/fulfilment/types';
import { Tabs, Tab } from '@jbhi-fi/lanyard-ui/components/Tabs';
import StoreLayout from '../../store/StoreLayout';
import { DEFAULT_CACHE_LIFETIME_SECONDS } from '../../localisation/components/product-fulfilment-info/constants';

export type AvailabilityVariantType = 'default' | 'delivery' | 'inStore';
export interface AvailabilityWrapperProps {
  variant?: AvailabilityVariantType;
  items: Array<FulfilmentOptionsLineItem>;
  maxDistance: number;
  isLIA?: boolean;
  LIAStore?: StoreLocation;
  isOpen: boolean;
  onClose: () => void;
  shouldShowDisplayModelStatus?: boolean;
  isPreOrder?: boolean;
  isDeliveryOnly?: boolean;
  isInStoreOnly?: boolean;
  productGroup?: string;
  onVariantChange?: (variant: AvailabilityVariantType) => void;
  location?: string;
}

const AvailabilityWrapper = ({
  location,
  variant = 'default',
  items,
  maxDistance = 200,
  isLIA,
  LIAStore,
  isOpen,
  onClose,
  shouldShowDisplayModelStatus = false,
  isPreOrder,
  isDeliveryOnly,
  isInStoreOnly,
  productGroup,
  onVariantChange,
}: AvailabilityWrapperProps) => {
  const [error, setError] = useState('');
  const [delivery, setDelivery] = useState<FulfilmentOptionsProps>();
  const [clickAndCollect, setClickAndCollect] = useState<FulfilmentOptionsProps>();
  const [pickUp, setPickUp] = useState<FulfilmentOptionsProps>();
  const [inStoreOptions, setInStoreOptions] = useState<Array<StoreAvailabilityDetails>>();
  const {
    deliveryLocation,
    selectedStore,
    isLocalisationEnabled,
    updateMySelectedStoreFromId,
    updateMyDeliveryLocation,
    yourStoreFulfilmentOptions,
    shouldCacheAutoRefresh,
    cacheLifetimeSeconds,
    setYourStoreFulfilmentOptions,
    setShouldUsePdpHybridPickupSlider,
  } = useLocalisationContext();
  const [initialValue, setInitialValue] = useState('');
  const shouldUseCache = !!shouldCacheAutoRefresh;
  const cacheTime = shouldUseCache ? cacheLifetimeSeconds * 1000 || DEFAULT_CACHE_LIFETIME_SECONDS * 1000 : Infinity;
  const [hasOutOfRangeStoreOptions, setHasOutOfRangeStoreOptions] = useState(false);
  const defaultAvailabilityPostcode = getLocal(LocalStorageKeys.AvailabilityPostcode);
  const defaultAvailabilitySuburb = getLocal(LocalStorageKeys.AvailabilitySuburb);
  const { tracker } = useTracking({
    context: trackingContext.localisation,
    category: 'localisation',
    group: 'availability',
  });
  const preSelectedStore = LIAStore || selectedStore;
  const fulfilmentClient = useRef(getFulfilmentClient()).current;
  const queryClient = useQueryClient();
  const { isReady: isRecaptchaReady, generateToken } = useRecaptchaContext();
  const [isLoading, setIsLoading] = useState(!isRecaptchaReady);
  const [internalActiveTab, setInternalActiveTab] = useState<AvailabilityVariantType | undefined>(undefined);

  const activeTab = internalActiveTab ?? variant;

  useEffect(() => {
    setInternalActiveTab(variant);
  }, [variant, isOpen]);

  const [showStoreDetails, setShowStoreDetails] = useState('');

  useEffect(() => {
    if (!isRecaptchaReady) return;
    if (isOpen && (deliveryLocation || preSelectedStore || defaultAvailabilityPostcode)) {
      const getDeliveryLocation = async () => {
        switch (activeTab) {
          case 'inStore':
            if (preSelectedStore) {
              await getFulfilment(preSelectedStore?.postcode, preSelectedStore?.suburb);
            }
            break;
          case 'delivery':
            if (deliveryLocation) {
              await getFulfilment(deliveryLocation?.postcode, deliveryLocation?.suburb);
            }
            break;
          // we shouldn't need a default case here when the old availability is decommissioned from cart page
          default:
            if (isLIA && LIAStore) {
              await getFulfilment(LIAStore.postcode, LIAStore.suburb);
              setInitialValue(LIAStore.aggregatedAddress);
            } else if (!isLIA && !deliveryLocation && preSelectedStore) {
              await getFulfilment(preSelectedStore?.postcode, preSelectedStore?.suburb);
              setInitialValue(preSelectedStore?.aggregatedAddress);
            } else if ((!isLIA && deliveryLocation) || defaultAvailabilityPostcode) {
              const isDeliveryLocationValid = isLocalisationEnabled && deliveryLocation?.postcode && deliveryLocation;
              await getFulfilment(
                isDeliveryLocationValid ? deliveryLocation.postcode : defaultAvailabilityPostcode,
                isDeliveryLocationValid ? deliveryLocation.suburb : defaultAvailabilitySuburb
              );
              setInitialValue((isLocalisationEnabled && deliveryLocation?.displayName) || defaultAvailabilityPostcode);
            }
            break;
        }
      };
      getDeliveryLocation();
      tracker.trackImpression({
        label: 'availability-open',
        source: 'availability',
        extraData: { activeTab },
      });
    } else {
      setIsLoading(false);
    }
  }, [isOpen, isRecaptchaReady, selectedStore, LIAStore, isLIA, activeTab]);

  const setFulfilmentOptionsForCartItems = async (data: FulfilmentOptionsResponse) => {
    const check = await transformPickupOptions({
      pickupOptions: data,
      selectedStore: preSelectedStore,
      isPreOrder: isPreOrder ? isPreOrder : false,
      isBulky: false,
      isBackOrder: false,
      maxDistance,
      shouldShowDisplayModelStatus,
      queryClient,
    });
    if (check.yourStore) {
      setYourStoreFulfilmentOptions({ ...check.yourStore, importantMessaging: check.importantMessaging });
    }
  };
  const availabilityOptions: AvailabilityOptions = {
    delivery: { ...delivery } as AvailabilityOptions['delivery'],
    clickAndCollect: { ...clickAndCollect } as AvailabilityOptions['clickAndCollect'],
    pickUp: { ...pickUp } as AvailabilityOptions['pickUp'],
  };
  const checkHasInstoreError = (options?: Array<StoreAvailabilityDetails>) => {
    return !options?.length
      ? location != 'cart'
        ? NO_INSTORE_AVAILABLE_MESSAGE
        : NO_INSTORE_AVAILABLE_MESSAGE_CART
      : '';
  };

  const getFulfilment = async (code: string, suburb: string) => {
    if (code === '' || !code) {
      return;
    }
    if (code.length !== 4) {
      return;
    }
    setError('');
    setIsLoading(true);

    const Address = { CountryCode: window.Jbhifi.storeLocation, Postcode: code, Suburb: suburb };

    const { data, error } = await queryClient.fetchQuery({
      queryKey: [
        'fulfilmentOptions',
        {
          countryCode: Address?.CountryCode,
          postcode: Address?.Postcode,
          suburb: Address?.Suburb,
        },
        items.map(item => ({
          sku: item.SKU ?? '',
          quantity: item.Quantity ?? 1,
          lineItemFreightPrice: item.LineItemFreightPrice ?? 0,
          giftCardType: item.GiftCardType ?? null,
          tieredIds: item.TieredIds,
        })),
      ],
      queryFn: async () => {
        const recaptchaToken = await generateToken?.(FULFILMENT_API_RECAPTCHA_ACTION);
        return await fulfilmentClient.getOptions(
          { Address, LineItems: items, requiresScheduledOptions: true },
          recaptchaToken ?? ''
        );
      },
      cacheTime,
      staleTime: cacheTime,
    });

    if (error || !data) {
      setError(`${ERROR_MESSAGE} ${activeTab === 'inStore' ? ERROR_MESSAGE_PICKUP : ERROR_MESSAGE_DELIVERY}`);
      window.console.log('Error while getting fulfilment data. ', error);
      setIsLoading(false);
    }
    const { delivery, clickAndCollect, cashAndCarry } = data || {};
    if (
      delivery?.presentation &&
      delivery.presentation.length > 0 &&
      delivery.presentation[0].type === 'PostcodeError'
    ) {
      setIsLoading(false);
      return;
    }
    switch (activeTab) {
      case 'delivery':
        setDelivery(delivery ? generateDeliveryOptions(delivery) : undefined);
        break;
      case 'inStore': {
        const storeOptions = data
          ? await getAggregatedStoreOptions({
              pickupOptions: data,
              maxDistance,
              shouldShowDisplayModelStatus,
              queryClient,
            })
          : [];

        if (location === 'cart' && data) {
          setFulfilmentOptionsForCartItems(data);
          setShouldUsePdpHybridPickupSlider(false);
        }

        setHasOutOfRangeStoreOptions(
          !!(
            !storeOptions?.length &&
            (clickAndCollect?.clickAndCollectOptions?.length || cashAndCarry?.cashAndCarryOptions?.length)
          )
        );
        setInStoreOptions(storeOptions);
        break;
      }
      default:
        setDelivery(delivery ? generateDeliveryOptions(delivery) : undefined);
        setClickAndCollect(
          clickAndCollect
            ? generateCashOrCollectOptions(clickAndCollect, OptionNames.ClickAndCollect, maxDistance)
            : undefined
        );
        setPickUp(
          cashAndCarry ? generateCashOrCollectOptions(cashAndCarry, OptionNames.CashAndCarry, maxDistance) : pickUp
        );
        setLocal(LocalStorageKeys.AvailabilityPostcode, code);
        setLocal(LocalStorageKeys.AvailabilitySuburb, suburb);
    }
    setIsLoading(false);
  };

  const handleLocationSelected = (location: DeliveryLocation) => {
    if (activeTab === 'delivery') {
      updateMyDeliveryLocation(location);
    }
    getFulfilment(location.postcode, location.suburb);
  };

  const onTabChange = (e: React.MouseEvent | React.KeyboardEvent | null, i: number | null, value: string) => {
    const updatedVariant = value as AvailabilityVariantType;
    setInternalActiveTab(updatedVariant);
    onVariantChange?.(updatedVariant);
  };

  const onStoreClick = (storeId: number) => {
    setShowStoreDetails(storeId.toString());
  };

  const getSelectedStoreUpdatedInfo = () => {
    const storeData = inStoreOptions?.find(store => store.id === preSelectedStore?.id);
    return { closingTime: storeData?.closingTime, isStoreOpenInNexthour: storeData?.isStoreOpenInNexthour };
  };

  const onPopUpDrawerClose = () => {
    setShowStoreDetails('');
    setInternalActiveTab(undefined);
    onClose();
  };

  const tabs = [
    <Tab label="In-store" value="inStore" key="inStore" variant="Spaced" icon="Store">
      <StoreAvailability
        selectedStore={{
          ...preSelectedStore,
          ...getSelectedStoreUpdatedInfo(),
        }}
        inStoreOptions={inStoreOptions?.filter(store => store.id !== preSelectedStore?.id)}
        isLoading={isLoading}
        yourStoreFulfilmentOptions={yourStoreFulfilmentOptions}
        isPreOrder={isPreOrder}
        error={checkHasInstoreError(inStoreOptions)}
        handleStoreLocationUpdated={handleLocationSelected}
        onClose={onPopUpDrawerClose}
        updateMySelectedStoreFromId={updateMySelectedStoreFromId}
        variant={activeTab}
        onStoreClick={onStoreClick}
        isDeliveryOnly={isDeliveryOnly}
        isErrorMessage={error}
        hasOutOfRangeStoreOptions={hasOutOfRangeStoreOptions}
      />
    </Tab>,
    <Tab label="Delivery" value="delivery" key="delivery" variant="Spaced" icon="Truck">
      <DeliveryAvailability
        deliveryLocation={deliveryLocation}
        isLoading={isLoading}
        deliveryOptions={availabilityOptions.delivery}
        error={
          !availabilityOptions.delivery.options?.length
            ? isInStoreOnly
              ? ONLY_INSTORE_AVAILABLE_MESSAGE
              : NO_DELIVERY_AVAILABLE_MESSAGE
            : ''
        }
        handleDeliveryLocationUpdated={handleLocationSelected}
        variant={activeTab}
        isErrorMessage={error}
        productGroup={productGroup}
      />
    </Tab>,
  ];

  const getStoreDetails = () => {
    return showStoreDetails?.length ? (
      <StoreLayout shopId={showStoreDetails} isPopOut={true} onBackClick={() => setShowStoreDetails('')} />
    ) : null;
  };

  const getAvailabilityComponent = () => {
    return activeTab === 'inStore' || activeTab === 'delivery' ? (
      <Tabs name="localisation" activeTab={activeTab} onChange={onTabChange}>
        {tabs}
      </Tabs>
    ) : error ? (
      <div className={styles.errorContainer}>{ERROR_MESSAGE_UNAVAILABLE}</div>
    ) : (
      <>
        <LocationSearch
          id="location-search"
          initialValue={initialValue}
          showUseMyLocationButton={false}
          gtmEventSource="default-availability"
          onLocationSelect={handleLocationSelected}
        />
        <Availability
          isLoading={isLoading}
          availabilityOptions={delivery || inStoreOptions ? availabilityOptions : undefined}
          IsLIA={!!LIAStore}
        />
      </>
    );
  };

  return (
    <PopoutDrawer
      anchor="right"
      title={!showStoreDetails ? 'Getting your item' : ''}
      isOpen={isOpen}
      onClose={onPopUpDrawerClose}
    >
      {!showStoreDetails ? getAvailabilityComponent() : getStoreDetails()}
    </PopoutDrawer>
  );
};

export default AvailabilityWrapper;
